import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LayoutDashboard, FolderKanban, BarChart3, Users, LogOut, User, Wrench, Menu, X, UserCog, Target, LayoutGrid, Settings, Contact, HelpCircle, Home, Folder, PlusCircle, Inbox, FileText, Clock } from 'lucide-react';
import { Button } from './ui/button';
import { NotificationBell } from './NotificationBell';
import { KPISidebar } from './KPISidebar';
import { GlobalSearch, SearchTrigger } from './GlobalSearch';
import { OnboardingTour } from './OnboardingTour';
import { ThemeToggle } from './ThemeToggle';
import { AIAssistantButton } from './AIAssistant';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [inboxCount, setInboxCount] = useState(0);
  const [hoursPending, setHoursPending] = useState(0);

  // ── Central permission check with fallback for stale sessions ──
  const FEATURE_DEFAULTS = {
    'admin':          ['dashboard','leads','leads_all','skrzynka_leadow','projects','clients','kanban','reports','kadry','users_admin','settings','kpi','archive','konfigurator_ofert','field_lead_form','profile','help'],
    'user':           ['dashboard','leads','leads_all','skrzynka_leadow','projects','clients','kanban','reports','kpi','archive','konfigurator_ofert','field_lead_form','profile','help'],
    'asystent_biura': ['dashboard','leads','leads_all','skrzynka_leadow','projects','clients','kanban','reports','kadry','kpi','archive','konfigurator_ofert','field_lead_form','profile','help'],
    'handlowiec_b2b': ['dashboard','leads','projects','clients','reports','kpi','field_lead_form','profile','help'],
    'montaz':         ['dashboard','profile','help'],
    'duch':           ['dashboard','projects','kanban','profile','help'],
    'guest':          ['dashboard','profile','help'],
    'lead_feeder':    ['field_lead_form'],
  };
  const computeFallbackFeatures = () => {
    const base = new Set(FEATURE_DEFAULTS[user?.role] || []);
    // Merge extra_permissions from stored user (even if available_features is missing)
    (user?.extra_permissions || []).forEach(f => base.add(f));
    // Subtract removed_permissions
    (user?.removed_permissions || []).forEach(f => base.delete(f));
    return [...base];
  };
  const userFeatures = user?.available_features?.length
    ? user.available_features
    : computeFallbackFeatures();
  const hasFeature = (feat) => userFeatures.includes(feat);

  // Fetch inbox count for eligible roles
  const canSeeInbox = userFeatures.includes('skrzynka_leadow');
  useEffect(() => {
    if (!canSeeInbox) return;
    const fetchCount = async () => {
      try {
        const res = await axios.get(`${API_URL}/lead-inbox/count`);
        setInboxCount(res.data.count || 0);
      } catch { /* ignore */ }
    };
    fetchCount();
    const interval = setInterval(fetchCount, 60000); // refresh every 60s
    return () => clearInterval(interval);
  }, [canSeeInbox]);

  const canSeeHours = userFeatures.includes('kadry');
  useEffect(() => {
    if (!canSeeHours) return;
    const fetchHours = () => axios.get(`${API_URL}/godziny/pending-count`).then((r) => setHoursPending(r.data.count || 0)).catch(() => {});
    fetchHours();
    const interval = setInterval(fetchHours, 60000);
    window.addEventListener('godziny:pending-changed', fetchHours);
    return () => { clearInterval(interval); window.removeEventListener('godziny:pending-changed', fetchHours); };
  }, [canSeeHours]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: 'Strona główna', shortLabel: 'Główna', testId: 'nav-dashboard' },
  ];

  // Build nav based on available_features (single source of truth from backend)
  if (hasFeature('leads')) {
    navItems.push({ path: '/leads', icon: Target, label: 'Leady', shortLabel: 'Leady', testId: 'nav-leads' });
  }
  if (hasFeature('skrzynka_leadow')) {
    navItems.push({ path: '/skrzynka-leadow', icon: Inbox, label: 'Skrzynka leadów', shortLabel: 'Skrzynka', testId: 'nav-inbox', badge: inboxCount > 0 ? inboxCount : null });
  }
  if (hasFeature('projects')) {
    const isB2B = user?.role === 'handlowiec_b2b';
    const isDuch = user?.role === 'duch';
    navItems.push({ path: '/projects', icon: FolderKanban, label: isB2B ? 'Moje Zlecenia' : isDuch ? 'Zlecenia (Demo)' : 'Zlecenia', shortLabel: 'Zlecenia', testId: 'nav-projects' });
  }
  if (hasFeature('clients')) {
    navItems.push({ path: '/klienci', icon: Contact, label: 'Klienci', shortLabel: 'Klienci', testId: 'nav-clients' });
  }
  if (hasFeature('kanban')) {
    const isDuch = user?.role === 'duch';
    navItems.push({ path: '/kanban', icon: LayoutGrid, label: isDuch ? 'Tablica (Demo)' : 'Tablica', shortLabel: 'Kanban', testId: 'nav-kanban' });
  }
  if (hasFeature('reports')) {
    navItems.push({ path: '/reports', icon: BarChart3, label: 'Raporty', shortLabel: 'Raporty', testId: 'nav-reports' });
  }
  if (hasFeature('kadry')) {
    navItems.push({ path: '/godziny', icon: Clock, label: 'Godziny pracy', shortLabel: 'Godziny', testId: 'nav-godziny', badge: hoursPending > 0 ? hoursPending : null });
  }
  if (hasFeature('users_admin')) {
    navItems.push({ path: '/users', icon: Users, label: 'Użytkownicy', shortLabel: 'Użytk.', testId: 'nav-users' });
  }
  if (hasFeature('konfigurator_ofert')) {
    navItems.push({ path: '/konfigurator', icon: FileText, label: 'Konfigurator ofert', shortLabel: 'Oferty', testId: 'nav-konfigurator' });
  }
  if (hasFeature('settings')) {
    navItems.push({ path: '/settings', icon: Settings, label: 'Ustawienia', shortLabel: 'Ustaw.', testId: 'nav-settings' });
  }
  if (hasFeature('help')) {
    navItems.push({ path: '/pomoc', icon: HelpCircle, label: 'Pomoc', shortLabel: 'Pomoc', testId: 'nav-help' });
  }

  // Step 2: Filter out user's hidden tabs
  const hiddenTabs = user?.hidden_tabs || [];
  const visibleNavItems = navItems.filter(item => !hiddenTabs.includes(item.path));

  // Fixed mobile bottom nav items (user-specified order)
  const mobileBottomNav = [
    { path: '/dashboard', icon: Home, label: 'Główna', testId: 'mobile-nav-home' },
    { path: '/leads', icon: Target, label: 'Leady', testId: 'mobile-nav-leads' },
    { path: '/lead-terenowy', icon: PlusCircle, label: 'Nowy Lead', testId: 'mobile-nav-new-lead', isCenter: true },
    { path: '/projects', icon: Folder, label: 'Zlecenia', testId: 'mobile-nav-projects' },
    { path: '/profile', icon: User, label: 'Profil', testId: 'mobile-nav-profile' },
  ];

  return (
    <div className="flex h-screen" style={{ backgroundColor: 'var(--crm-bg-main)' }}>
      {/* Desktop Sidebar - hidden on mobile */}
      <aside className="hidden md:flex w-64 flex-col" style={{ backgroundColor: 'var(--crm-bg-sidebar)', borderRight: '1px solid var(--crm-border)' }}>
        <div className="p-5" style={{ borderBottom: '1px solid var(--crm-border)' }}>
          <div className="flex items-center justify-between">
            <Link to="/dashboard" className="flex items-center">
              <img src="/logo.png" alt="Trendhomes" className="h-10 w-auto object-contain max-w-[120px] dark:brightness-100 brightness-0" />
            </Link>
            <div className="flex items-center gap-1">
              <ThemeToggle />
              <AIAssistantButton />
              <NotificationBell />
            </div>
          </div>
          <div className="mt-3 [&>button]:w-full">
            <SearchTrigger onClick={() => setSearchOpen(true)} />
          </div>
        </div>
        
        <nav className="flex-1 p-4 space-y-1">
          {visibleNavItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={item.testId}
                className={`flex items-center gap-2 pl-4 pr-3 h-11 rounded-md transition-colors duration-200 ${
                  isActive
                    ? 'bg-[#FF6B35] text-white'
                    : 'text-[var(--crm-text-secondary)] hover:bg-[var(--crm-bg-card-hover)] hover:text-[var(--crm-text-primary)]'
                }`}
              >
                <Icon className="w-5 h-5 shrink-0" />
                <span className="font-medium flex-1 truncate whitespace-nowrap text-sm">{item.shortLabel || item.label}</span>
                {item.badge && (
                  <span className="h-5 min-w-[20px] max-w-[36px] flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold px-1.5 shrink-0 leading-none" data-testid="inbox-badge">
                    {item.badge > 99 ? '99+' : item.badge}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>

        {/* KPI Sidebar Widget - only for non-montaz roles */}
        {user?.role !== 'montaz' && user?.role !== 'guest' && (
          <KPISidebar />
        )}

        <div className="p-4" style={{ borderTop: '1px solid var(--crm-border)' }}>
          <div className="flex items-center gap-3 mb-3 p-3 rounded-md cursor-pointer" style={{ backgroundColor: 'var(--crm-bg-card-hover)' }} onClick={() => navigate('/profile')}>
            <div className="w-8 h-8 rounded-full overflow-hidden flex items-center justify-center" style={{ backgroundColor: 'var(--crm-bg-btn-secondary)' }}>
              {user?.avatar_url ? (
                <img src={user.avatar_url} alt="Avatar" className="w-full h-full object-cover" />
              ) : (
                <User className="w-4 h-4 text-[var(--crm-text-primary)]" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[var(--crm-text-primary)] text-sm font-medium truncate">{user?.email}</p>
              <p className="text-[var(--crm-text-muted)] text-xs">{user?.initials} • {user?.role}</p>
            </div>
          </div>
          <Button
            onClick={handleLogout}
            data-testid="logout-button"
            className="w-full bg-transparent hover:bg-[var(--crm-bg-card-hover)] text-[var(--crm-text-secondary)] hover:text-[var(--crm-text-primary)] border"
            style={{ borderColor: 'var(--crm-border)' }}
          >
            <LogOut className="w-4 h-4 mr-2" />
            Wyloguj
          </Button>
        </div>
      </aside>

      {/* Mobile Header - visible only on mobile */}
      <div className="fixed top-0 left-0 right-0 z-50 md:hidden">
        <div className="px-4 py-2" style={{ backgroundColor: 'var(--crm-bg-sidebar)', borderBottom: '1px solid var(--crm-border)' }}>
          <div className="flex items-center justify-between">
            <Link to="/dashboard" className="flex items-center">
              <img src="/logo.png" alt="Trendhomes" className="h-9 w-auto object-contain max-w-[100px] dark:brightness-100 brightness-0" />
            </Link>
            <div className="flex items-center gap-1">
              <SearchTrigger onClick={() => setSearchOpen(true)} />
              <ThemeToggle />
              <AIAssistantButton />
              <NotificationBell />
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2 text-[var(--crm-text-secondary)] hover:text-white"
                data-testid="mobile-menu-toggle"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile dropdown menu - extra nav items not in bottom nav */}
        {mobileMenuOpen && (
          <div className="px-4 py-2 shadow-xl max-h-[60vh] overflow-y-auto" style={{ backgroundColor: 'var(--crm-bg-sidebar)', borderBottom: '1px solid var(--crm-border)' }}>
            <div className="flex items-center gap-3 p-3 mb-2 rounded-md" style={{ backgroundColor: 'var(--crm-bg-card-hover)' }} onClick={() => { navigate('/profile'); setMobileMenuOpen(false); }}>
              <div className="w-10 h-10 rounded-full overflow-hidden flex items-center justify-center" style={{ backgroundColor: 'var(--crm-bg-btn-secondary)' }}>
                {user?.avatar_url ? (
                  <img src={user.avatar_url} alt="Avatar" className="w-full h-full object-cover" />
                ) : (
                  <User className="w-5 h-5 text-[var(--crm-text-primary)]" />
                )}
              </div>
              <div className="flex-1">
                <p className="text-[var(--crm-text-primary)] font-medium">{user?.email}</p>
                <p className="text-[var(--crm-text-muted)] text-sm">{user?.initials} • {user?.role}</p>
              </div>
            </div>
            <div className="space-y-1 mb-2">
              {visibleNavItems.filter(item =>
                !['/dashboard', '/projects', '/klienci', '/profile', '/leads'].includes(item.path)
              ).map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    data-testid={`mobile-menu-${item.testId}`}
                    className={`flex items-center gap-3 px-4 py-3 rounded-md transition-colors ${
                      isActive ? 'bg-[#FF6B35] text-white' : 'text-[var(--crm-text-secondary)] hover:bg-[var(--crm-bg-card-hover)]'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="font-medium text-sm">{item.label}</span>
                  </Link>
                );
              })}
            </div>
            <Button
              onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
              className="w-full bg-transparent hover:bg-[var(--crm-bg-card-hover)] text-[var(--crm-text-secondary)] hover:text-[var(--crm-text-primary)] border"
              style={{ borderColor: 'var(--crm-border)' }}
            >
              <LogOut className="w-4 h-4 mr-2" />
              Wyloguj
            </Button>
          </div>
        )}
      </div>

      {/* Main Content */}
      <main className="flex-1 overflow-auto pb-24 md:pb-0 pt-14 md:pt-0">
        {children}
      </main>

      {/* Mobile Bottom Navigation - visible only on mobile */}
      <nav className="fixed bottom-0 left-0 right-0 z-50 md:hidden" data-testid="mobile-bottom-nav">
        <div className="px-2 pb-[env(safe-area-inset-bottom,8px)]" style={{ backgroundColor: 'var(--crm-bg-sidebar)', borderTop: '1px solid var(--crm-border)' }}>
          <div className="flex items-end justify-around pt-2 pb-2">
            {mobileBottomNav.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');

              if (item.isCenter) {
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    data-testid={item.testId}
                    className="flex flex-col items-center justify-center -mt-7 relative"
                  >
                    <div className="w-14 h-14 rounded-full bg-[#F59E0B] shadow-lg shadow-amber-500/30 flex items-center justify-center border-4 transition-transform active:scale-95" style={{ borderColor: 'var(--crm-bg-sidebar)' }}>
                      <PlusCircle className="w-7 h-7 text-white" />
                    </div>
                    <span className="text-[10px] mt-1 font-semibold text-[#F59E0B]">
                      {item.label}
                    </span>
                  </Link>
                );
              }

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={item.testId}
                  className={`flex flex-col items-center justify-center py-1 px-2 min-w-[56px] transition-colors ${
                    isActive ? 'text-[#FF6B35]' : 'text-[var(--crm-text-secondary)]'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="text-[10px] mt-1 font-medium">{item.label}</span>
                </Link>
              );
            })}
          </div>
        </div>
      </nav>

      {/* Global Search Modal */}
      <GlobalSearch open={searchOpen} onClose={() => setSearchOpen(false)} />

      {/* Onboarding Tour */}
      <OnboardingTour />
    </div>
  );
};
